import type {
  EnablementsDto,
  EntitlementDto,
  ProductEnablementDto,
} from '../api/entitlementsApi';
import { PLAN_ENABLEMENT_FLAG_NAMES_LIST } from '../config/planEnablement.config';
import {
  createPlanProductEnablementChecker,
  type GetFlagFn,
  getMergedPlanEnablement,
  hasMergedPlanEnablementData,
  isFeatureEnabledForProducts,
} from '../enablement/enablement.helpers';
import type { PlanFeatureEnablementValue } from '../enablement/enablement.types';

export type EnablementsLicense = ProductEnablementDto['licenseType'];

/** Optional getFlag from useFeatureFlags(); when provided, plan enablement flags gate feature access. */
export type { GetFlagFn };

enum AssetAccessor {
  VINS = 'VINS',
  PERIPHERALS = 'PERIPHERALS',
}

/**
 * Whether the org-level product entitlements payload lists the feature (license / enablements data only; no plan flags).
 *
 * @param featureCode - Feature code to look up
 * @param orgProductEntitlements - `entitlements` array from org entitlements response
 */
export function isFeatureEntitledFromOrgProductEntitlements(
  featureCode: string,
  orgProductEntitlements: EntitlementDto[] | undefined
): boolean {
  return (
    orgProductEntitlements?.some((entitlement) =>
      entitlement.product.features.includes(featureCode)
    ) ?? false
  );
}

/**
 * Whether the org-level product entitlements payload lists the product (license / enablements data only; no plan flags).
 *
 * @param productCode - Product code to look up
 * @param orgProductEntitlements - `entitlements` array from org entitlements response
 */
export function isProductEntitledFromOrgProductEntitlements(
  productCode: string,
  orgProductEntitlements: EntitlementDto[] | undefined
): boolean {
  return (
    orgProductEntitlements?.some(
      (entitlement) => entitlement.product.code === productCode
    ) ?? false
  );
}

/**
 * Whether merged plan enablement allows `featureCode` for at least one org-level product whose
 * license payload lists that feature (pairwise `(product.code, featureCode)`). Avoids treating the
 * feature as on when it appears only under a different product in LD (duplicate feature codes
 * across products / OSOF-style catalogs).
 * When the merged plan map is empty, returns **`false`** (same strict empty **P** as {@link isFeatureEnabledForProducts}).
 *
 * @param mergedPlanEnablement - Result of {@link getMergedPlanEnablement}
 * @param featureCode - Feature code to check
 * @param orgProductEntitlements - `entitlements` from org entitlements response
 */
export function isFeatureEnabledForOrgProductEntitlements(
  mergedPlanEnablement: PlanFeatureEnablementValue,
  featureCode: string,
  orgProductEntitlements: EntitlementDto[] | undefined
): boolean {
  if (!hasMergedPlanEnablementData(mergedPlanEnablement)) {
    return false;
  }
  if (!orgProductEntitlements?.length) {
    return false;
  }
  return orgProductEntitlements.some(
    (entitlement) =>
      entitlement.product.features.includes(featureCode) &&
      mergedPlanEnablement[entitlement.product.code]?.includes(featureCode) ===
        true
  );
}

/**
 * Product codes attached to an asset in enablements data (no plan merge).
 *
 * @param data - Per-asset enablements DTO
 * @param assetId - Vehicle or peripheral id
 * @param assetType - `'vehicle'` uses VINS; `'peripheral'` uses PERIPHERALS
 */
export function getAssetProductCodesFromEnablementsData(
  data: EnablementsDto | undefined,
  assetId: string,
  assetType: 'vehicle' | 'peripheral'
): string[] {
  const accessor =
    assetType === 'vehicle' ? AssetAccessor.VINS : AssetAccessor.PERIPHERALS;
  return data?.products?.[accessor]?.[assetId] ?? [];
}

/**
 * Whether enablements data lists the feature for the asset (no plan flags). Used for **`isEntitledFromData`**-style checks alongside {@link hasFeatureAccess}.
 *
 * @param featureCode - Feature code
 * @param assetId - Asset id (required; org-level checks use {@link isFeatureEntitledFromOrgProductEntitlements})
 * @param data - Enablements payload (`features` by accessor)
 * @param assetType - Vehicle (default) or peripheral
 */
export function isFeatureEntitledFromEnablementsData(
  featureCode: string,
  assetId: string,
  data: EnablementsDto | undefined,
  assetType: 'vehicle' | 'peripheral' = 'vehicle'
): boolean {
  if (!data || !assetId) {
    return false;
  }
  if (!assetType || assetType === 'vehicle') {
    const vehicleFeatures =
      data.features?.[AssetAccessor.VINS]?.[assetId] ?? [];
    return vehicleFeatures.includes(featureCode);
  }
  if (assetType === 'peripheral') {
    const peripheralFeatures =
      data.features?.[AssetAccessor.PERIPHERALS]?.[assetId] ?? [];
    return peripheralFeatures.includes(featureCode);
  }
  return false;
}

/**
 * Whether enablements data lists the product for the asset (no plan flags).
 *
 * @param productCode - Product code
 * @param assetId - Asset id
 * @param data - Enablements payload (`products` by accessor)
 * @param assetType - Vehicle (default) or peripheral
 */
export function isProductEntitledFromEnablementsData(
  productCode: string,
  assetId: string,
  data: EnablementsDto | undefined,
  assetType: 'vehicle' | 'peripheral' = 'vehicle'
): boolean {
  if (!data || !assetId) {
    return false;
  }
  if (!assetType || assetType === 'vehicle') {
    const vehicleProducts =
      data.products?.[AssetAccessor.VINS]?.[assetId] ?? [];
    return vehicleProducts.includes(productCode);
  }
  if (assetType === 'peripheral') {
    const peripheralProducts =
      data.products?.[AssetAccessor.PERIPHERALS]?.[assetId] ?? [];
    return peripheralProducts.includes(productCode);
  }
  return false;
}

/**
 * Determines if a specific feature is accessible for a given asset (vehicle or peripheral).
 * When `getFlag` is provided, result is gated by plan enablement flags: the feature must be
 * enabled for at least one of the products that this asset is entitled to (same plan/product
 * as in the enablements data), not just for any plan. If the merged plan map **P** is empty,
 * returns false (entitlement from data alone is not sufficient). If **P** is non-empty but the
 * asset has no product codes in enablements, returns false (no `(product, feature)` pair).
 *
 * @param featureCode - The code of the feature to check access for.
 * @param assetId - The unique identifier of the asset (vehicle or peripheral).
 * @param data - The enablements data containing feature access information.
 * @param assetType - The type of asset ('vehicle' or 'peripheral'). Defaults to 'vehicle'.
 * @param getFlag - Optional. From useFeatureFlags().getFlag; when provided, plan enablement is applied.
 * @returns `true` if the asset has access to the specified feature, otherwise `false`.
 */
export const checkFeatureAccess = (
  featureCode: string,
  assetId?: string,
  data?: EnablementsDto,
  assetType: 'vehicle' | 'peripheral' = 'vehicle',
  getFlag?: GetFlagFn
) => {
  if (!assetId) return false;
  const hasEntitlementFromData = isFeatureEntitledFromEnablementsData(
    featureCode,
    assetId,
    data,
    assetType
  );
  if (!hasEntitlementFromData) return false;

  if (!getFlag) {
    return true;
  }
  const assetProducts = getAssetProductCodesFromEnablementsData(
    data,
    assetId,
    assetType
  );
  const merged = getMergedPlanEnablement(
    getFlag,
    PLAN_ENABLEMENT_FLAG_NAMES_LIST
  );
  return (
    hasEntitlementFromData &&
    isFeatureEnabledForProducts(merged, featureCode, assetProducts)
  );
};

/**
 * Returns true when at least one asset has access to the feature.
 * Accepts a list of asset ids (e.g. VIN + connected devices keyed under VINS).
 */
export const hasFeatureAccess = (
  featureCode: string,
  assetIds: string[],
  data?: EnablementsDto,
  assetType: 'vehicle' | 'peripheral' = 'vehicle',
  getFlag?: GetFlagFn
): boolean =>
  assetIds.some((id) =>
    checkFeatureAccess(featureCode, id, data, assetType, getFlag)
  );

/**
 * Returns true when the VIN (VINS) or any connected device (PERIPHERALS) has the feature.
 */
export function hasFeatureAccessForVehicleAndDevices(
  featureCode: string,
  vin: string | undefined,
  peripheralIds: readonly string[],
  data: EnablementsDto | undefined,
  getFlag?: GetFlagFn
): boolean {
  if (vin && checkFeatureAccess(featureCode, vin, data, 'vehicle', getFlag)) {
    return true;
  }
  return peripheralIds.some((deviceId) =>
    checkFeatureAccess(featureCode, deviceId, data, 'peripheral', getFlag)
  );
}

/**
 * Checks if a specific product is accessible for a given asset (vehicle or peripheral).
 * When `getFlag` is provided, result is also gated by plan enablement flags (product must appear
 * in **P** with a non-empty feature list). If **P** is empty, returns null even when entitled from data.
 *
 * @param productCode - The code of the product to check access for.
 * @param assetId - The unique identifier of the asset (vehicle or peripheral).
 * @param data - The enablements data containing product access information.
 * @param assetType - The type of asset ('vehicle' or 'peripheral'). Defaults to 'vehicle'.
 * @param getFlag - Optional. From useFeatureFlags().getFlag; when provided, plan enablement is applied.
 * @returns {EnablementsLicense | null} - The license type of the product if asset has access, otherwise null.
 */
export const hasProductAccess = (
  productCode: string,
  assetId?: string,
  data?: EnablementsDto,
  assetType: 'vehicle' | 'peripheral' = 'vehicle',
  getFlag?: GetFlagFn
): EnablementsLicense | null => {
  if (!assetId) return null;

  const hasEntitlementFromData = isProductEntitledFromEnablementsData(
    productCode,
    assetId,
    data,
    assetType
  );
  if (!hasEntitlementFromData) return null;

  const accessor =
    assetType === 'vehicle' ? AssetAccessor.VINS : AssetAccessor.PERIPHERALS;

  const license: EnablementsLicense | null =
    data?.productDetails?.[accessor]?.[assetId]?.find(
      (enablement) => enablement.code === productCode
    )?.licenseType ?? null;

  if (!getFlag) {
    return license;
  }

  const isProductEnabledByPlanFlags = createPlanProductEnablementChecker(
    getFlag,
    PLAN_ENABLEMENT_FLAG_NAMES_LIST
  );
  return license && isProductEnabledByPlanFlags(productCode) ? license : null;
};

/**
 * Returns the licensed products for an asset that are eligible based on plan enablement flags.
 *
 * Without `getFlag`: returns all productDetails for the asset (no flag gating).
 * With `getFlag`: keeps only products whose code is a key in the merged plan enablement map.
 * Empty map (`{}`) → returns `[]`.
 *
 * @param assetId - The asset identifier (VIN or peripheral id)
 * @param data - The enablements data
 * @param assetType - Vehicle (default) or peripheral
 * @param getFlag - Optional. From useFeatureFlags().getFlag; when provided, plan enablement is applied.
 * @returns Filtered array of ProductEnablementDto (code + licenseType) for accessible products.
 */
export function getEligibleProductDetails(
  assetId: string | undefined,
  data: EnablementsDto | undefined,
  assetType: 'vehicle' | 'peripheral' = 'vehicle',
  getFlag?: GetFlagFn
): ProductEnablementDto[] {
  if (!assetId || !data) return [];

  const accessor =
    assetType === 'vehicle' ? AssetAccessor.VINS : AssetAccessor.PERIPHERALS;
  const details = data.productDetails?.[accessor]?.[assetId] ?? [];

  if (!details.length || !getFlag) {
    return details;
  }

  const planEnablementMap = getMergedPlanEnablement(
    getFlag,
    PLAN_ENABLEMENT_FLAG_NAMES_LIST
  );

  if (!hasMergedPlanEnablementData(planEnablementMap)) {
    return [];
  }

  return details.filter(({ code }) => code in planEnablementMap);
}

const uniqueProductEnablements = (
  enablements: ProductEnablementDto[]
): ProductEnablementDto[] => [
  ...new Map(
    enablements.map((plan) => [`${plan.code}-${plan.licenseType}`, plan])
  ).values(),
];

/**
 * Licensed products for a vehicle VIN plus connected peripheral device ids.
 * VIN plans use {@link AssetAccessor.VINS}; each device id uses {@link AssetAccessor.PERIPHERALS}.
 */
export function getEligibleProductDetailsForVehicleAndDevices(
  vin: string | undefined,
  peripheralIds: readonly string[],
  data: EnablementsDto | undefined,
  getFlag?: GetFlagFn
): ProductEnablementDto[] {
  const vinPlans = getEligibleProductDetails(vin, data, 'vehicle', getFlag);
  const devicePlans = peripheralIds.flatMap((deviceId) =>
    getEligibleProductDetails(deviceId, data, 'peripheral', getFlag)
  );

  return uniqueProductEnablements([...vinPlans, ...devicePlans]);
}
